import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { PatientModel } from '../../../core/api/models/patient-model';
import { FormControl, Validators } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { PatientTargetModel } from '../../../core/api/models/patient-target-model';
import { SupervisorApiService } from '../../../core/api/services/supervisor-api.service';
import { AlertService } from '../../../shared/services/alert.service';
import { SupervisorHelperService } from '../../../supervisor/supervisor.helper.service';
import { AuthService } from '../../../auth/auth.service';
import { isOpenEndedFluencyRate, MAX_BULK_FREQUENCY_OCCURRENCES, targetIsEchoic, targetIsPromptBased } from '../../../shared/utils/utils';
import {
  ALREADY_SAVED_BULK_FREQUENCY_MESSAGE,
  bulkFrequencyErrorMessage,
  bulkFrequencyOccurrencesValidators,
  bulkFrequencyRangeError,
  bulkFrequencySaveKey,
  bulkFrequencySuccessMessage,
  resolveBulkFrequencyRecordedDates,
  saveBulkFrequencyEntries,
  showBulkFrequencyEndDate,
  updateBulkFrequencyEndDateValidators,
} from '../../../shared/utils/bulk-frequency';
import { PatientTargetDataModel } from '../../../core/api/models/patient-target-data-model';
import { PromptCodeModel } from '../../../core/api/models/prompt-code-model';
import { SelectItem } from 'primeng/api';
import { Utils } from '../../../core/utils';
import { UserModel } from '../../../core/api/models/user-model';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { GraphApiService } from '../../../core/api/services/graph-api.service';
import { PatientSessionModel } from '../../../core/api/models/patient-session-model';
import { PatientTargetDataToileting, SessionSimpleDataModel, TargetType, DataTakerStaffDataModel } from '../../../core/api/models';
import { PatientBehaviorModel } from '../../../core/api/models/patient-behavior-model';
import { PatientBehaviorAntecedentModel, PatientBehaviorConsequenceModel, PatientBehaviorDataModel, PatientBehaviorLocationModel, PatientBehaviorResponseModel, PatientBehaviorSeverityModel, PatientBehaviorType } from '../../../core/api/models';
import { forkJoin, Observable } from 'rxjs';
import { Permissions } from "../../../core/api/models";
import { PatientDataFacadeService } from '../../../core/services/patient-data-facade.service';

@Component({
  selector: 'app-add-data',
  templateUrl: './add-data.component.html',
  styleUrl: './add-data.component.css',
  standalone: false,
})
export class AddDataComponent implements OnInit {

  @Input({ required: true }) selectedPatientID!: number;
  @Input({ required: false }) patientSession: PatientSessionModel | null = null;
  @Input({ required: false }) target: PatientTargetModel | null = null;
  @Input({ required: false }) behavior: PatientBehaviorModel | null = null;
  @Input({ required: false }) hideTargetSelector: boolean = false;
  @Input({ required: false }) hideBehaviorSelector: boolean = false;
  @Input({ required: false }) hideSessionSelector: boolean = false;
  @Output() close = new EventEmitter<boolean>();

  isLoading = signal(false);

  // Target Form
  addTargetForm = new FormGroup({
    recordedDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
    target: new FormControl<PatientTargetModel | null>(null, { validators: [Validators.required] }),
    selectedPromptCode: new FormControl<PromptCodeModel | null>(null),
    selectedEchoicRate: new FormControl<string | null>(null),
    textValue: new FormControl<string | null>(null),
    duration: new FormControl<number | null>(null),
    happened: new FormControl<boolean>(false),
    incorrectCount: new FormControl<number | null>(null),
    correctCount: new FormControl<number | null>(null),
    occurrenceCount: new FormControl<number | null>(null),
    selectedStaff: new FormControl<SelectItem<DataTakerStaffDataModel> | null>(null),
    frequencyEndDate: new FormControl<string | null>(null),
    frequencyOccurrences: new FormControl<number | null>(1, {
      validators: bulkFrequencyOccurrencesValidators(),
    }),
    intensityValue: new FormControl<number | null>(null),
  });

  // Behavior Form
  addBehaviorForm = new FormGroup({
    recordedDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
    behavior: new FormControl<PatientBehaviorModel | null>(null, { validators: [Validators.required] }),
    selectedAntecedent: new FormControl<PatientBehaviorAntecedentModel[] | null>([]),
    antecedentOther: new FormControl<string | null>(null),
    selectedSeverity: new FormControl<PatientBehaviorSeverityModel | null>(null),
    selectedResponse: new FormControl<PatientBehaviorResponseModel | null>(null),
    selectedLocation: new FormControl<PatientBehaviorLocationModel | null>(null),
    selectedConsequence: new FormControl<PatientBehaviorConsequenceModel[] | null>([]),
    consequenceOther: new FormControl<string | null>(null),
    textValue: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    duration: new FormControl<number | null>(null),
    happenedDuringInterval: new FormControl<boolean>(false),
    intensityValue: new FormControl<number | null>(null),
    selectedStaff: new FormControl<SelectItem<DataTakerStaffDataModel> | null>(null),
    frequencyEndDate: new FormControl<string | null>(null),
    frequencyOccurrences: new FormControl<number | null>(1, {
      validators: bulkFrequencyOccurrencesValidators(),
    }),
  });

  titleName: string = 'Add Data';
  actionName: string = 'Add';

  // Target related properties
  targets = signal<SelectItem[]>([]);
  selectedTarget = signal<PatientTargetModel | null>(null);
  targetStaffMembers = signal<DataTakerStaffDataModel[]>([]);
  targetStaffOptions = signal<SelectItem<DataTakerStaffDataModel>[]>([]);

  // Behavior related properties
  behaviors = signal<SelectItem[]>([]);
  behaviorStaffMembers = signal<DataTakerStaffDataModel[]>([]);
  behaviorStaffOptions = signal<SelectItem<DataTakerStaffDataModel>[]>([]);
  antecedents = signal<PatientBehaviorAntecedentModel[]>([]);
  consequences = signal<PatientBehaviorConsequenceModel[]>([]);
  severities = signal<PatientBehaviorSeverityModel[]>([]);
  responses = signal<PatientBehaviorResponseModel[]>([]);
  locations = signal<PatientBehaviorLocationModel[]>([]);

  selectedBehavior = signal<PatientBehaviorModel | null>(null);
  selectedAntecedents = signal<PatientBehaviorAntecedentModel[] | null>([]);
  selectedConsequences = signal<PatientBehaviorConsequenceModel[] | null>([]);
  selectedSeverity = signal<PatientBehaviorSeverityModel | null>(null);
  selectedResponse = signal<PatientBehaviorResponseModel | null>(null);
  selectedLocation = signal<PatientBehaviorLocationModel | null>(null);

  // Shared properties
  sessions = signal<SelectItem<SessionSimpleDataModel>[]>([]);
  selectedSession: SessionSimpleDataModel | null = null;

  // toileting
  urinationOn = signal<boolean>(false);
  urinationOff = signal<boolean>(false);
  bowelMovementOn = signal<boolean>(false);
  bowelMovementOff = signal<boolean>(false);
  noVoid = signal<boolean>(false);

  user: UserModel | null = null;

  creatingSession = signal<boolean>(false);
  newSessionStartDate: string | null = null;
  newSessionEndDate: string | null = null;
  readonly maxBulkFrequencyOccurrences = MAX_BULK_FREQUENCY_OCCURRENCES;
  private savedFrequencyKeys = new Set<string>();

  activeTabIndex = 0; // 0: Target, 1: Behavior

  constructor(
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private graphApiService: GraphApiService,
    private alertService: AlertService,
    private authService: AuthService,
    private patientDataFacade: PatientDataFacadeService
  ) {

  }

  ngOnInit(): void {

    this.authService.user().subscribe((user) => {
      this.user = user;
    });

    if (this.target) {
      this.selectedTarget.set(this.target);
      this.addTargetForm.controls.target.setValue(this.target);
      this.defaultFluencyDuration();
    }

    if (this.behavior) {
      this.selectedBehavior.set(this.behavior);
      this.addBehaviorForm.controls.behavior.setValue(this.behavior);
    }

    if (this.patientSession) {
      this.hideSessionSelector = true;
      this.selectedSession = this.patientSession;
    }

    if (!this.hideSessionSelector) {
      this.loadSessions();
    }

    this.loadAllData();
    this.addTargetForm.controls.frequencyOccurrences.valueChanges.subscribe(() => {
      this.updateTargetFrequencyEndDateValidators();
    });
    this.addBehaviorForm.controls.frequencyOccurrences.valueChanges.subscribe(() => {
      this.updateFrequencyEndDateValidators();
    });
    this.updateTargetFrequencyEndDateValidators();
    this.updateFrequencyEndDateValidators();
  }

  loadAllData() {
    this.isLoading.set(true);

    const targetRequest = this.patientDataFacade.getTargets(this.selectedPatientID ?? 0, { onlyActive: false });
    const behaviorRequest = this.staffApiService.GetPatientBehaviors({ patientID: this.selectedPatientID ?? 0 });
    const antecedentRequest = this.staffApiService.GetPatientBehaviorAntecedents({ patientID: this.selectedPatientID ?? 0 });
    const consequenceRequest = this.staffApiService.GetPatientBehaviorConsequences({ patientID: this.selectedPatientID ?? 0 });
    const severityRequest = this.staffApiService.GetPatientBehaviorSeverities({});
    const responseRequest = this.staffApiService.GetPatientBehaviorResponses({});
    const locationRequest = this.staffApiService.GetPatientBehaviorLocations({});

    forkJoin({
      targets: targetRequest,
      behaviors: behaviorRequest,
      antecedents: antecedentRequest, 
      consequences: consequenceRequest,
      severities: severityRequest,
      responses: responseRequest,
      locations: locationRequest
    }).subscribe({
      next: (results) => {
        this.handleIncomingTargetsForAddData(results.targets);
        this.behaviors.set(results.behaviors.filter((b) => !b.IsDeleted && !b.IsArchived).map((b) => ({ 
          value: b, 
          label: this.displayNameForBehavior(b) 
        })));

        this.antecedents.set(results.antecedents);
        this.consequences.set(results.consequences);
        this.severities.set(results.severities);
        this.responses.set(results.responses);
        this.locations.set(results.locations);

        // Set initial recorded date from session start date if available, otherwise use current date
        const initialRecordedDate = this.getSessionStartDateForRecordedDate();

        this.addTargetForm.controls.recordedDate.setValue(initialRecordedDate);
        this.addTargetForm.controls.recordedDate.updateValueAndValidity();
        this.addBehaviorForm.controls.recordedDate.setValue(initialRecordedDate);
        this.addBehaviorForm.controls.recordedDate.updateValueAndValidity();

        // Load staff members if target or behavior is already selected
        if (this.selectedTarget()?.ID) {
          this.loadTargetStaffMembersForSelection();
        }
        if (this.selectedBehavior()?.ID) {
          this.loadBehaviorStaffMembersForSelection();
        }

        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  canSelectStaff() {
    return this.authService.userInfo?.Permissions?.includes(Permissions.SparkApp_Supervisor);
  }

  loadSessions() {
    this.isLoading.set(true);
    this.staffApiService.GetPatientSessionSimpleList({ patientID: this.selectedPatientID ?? 0 }).subscribe({
      next: (sessions) => {
        this.sessions.set(sessions.map((s) => ({ value: s, label: this.formattedDates(s.StartedDate, s.FinishedDate) + (s.ServiceID ? (' (Service: #' + s.ServiceID + ' - ' + (s.Staffs?.map(st => st.FullName ?? '').join(', ') ?? []) + ')') : '') })));
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  showCreateSession() {
    this.creatingSession.set(true);
    this.selectedSession = null;
  }

  hideCreateSession() {
    this.creatingSession.set(false);
    this.newSessionStartDate = null;
    this.newSessionEndDate = null;
  }

  formattedDates(startDate: string | null | undefined, endDate: string | null | undefined) {
    if (!startDate) {
      return 'Not started';
    }

    if (!endDate) {
      return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    }

    //if on the same day, return the date and 2 times
    if (new Date(startDate).toDateString() === new Date(endDate).toDateString()) {
      let result = new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
      //add end time to the result
      result += ' - ' + new Date(endDate).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      return result;
    }

    //date in fomrat MM/DD/YYYY hh:mm AM/PM
    return new Date(startDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) + ' - ' + new Date(endDate).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  }

  changedSession(event: any) {
    console.log(event);
    
    // Update selectedSession when session changes
    this.selectedSession = event.value;
    
    // Update recorded date when session changes
    if (event.value?.StartedDate) {
      const sessionStartDate = new Date(event.value.StartedDate);
      const newRecordedDate = new Date(sessionStartDate.getTime() - (sessionStartDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
      
      this.addTargetForm.controls.recordedDate.setValue(newRecordedDate);
      this.addTargetForm.controls.recordedDate.updateValueAndValidity();
      this.addBehaviorForm.controls.recordedDate.setValue(newRecordedDate);
      this.addBehaviorForm.controls.recordedDate.updateValueAndValidity();
    }
    
    // Reload staff members if target or behavior is already selected
    // This ensures staff from the selected session are included
    if (this.selectedTarget()) {
      this.loadTargetStaffMembersForSelection();
    }
    if (this.selectedBehavior()) {
      this.loadBehaviorStaffMembersForSelection();
    }
  }

  // Target related methods
  handleIncomingTargetsForAddData(targets: PatientTargetModel[]) {
    // filter out targets that are groupings of other targets
    const targetsWithSubtargets = targets.filter((t) => t.SubTargets && t.SubTargets.length > 0);

    let filteredTargets = targets.filter((t) => !targetsWithSubtargets.includes(t));

    // filter out deleted or archived targets
    filteredTargets = filteredTargets.filter((t) => !t.IsDeleted && !t.IsArchived);

    this.targets.set(filteredTargets.map((t) => ({ value: t, label: this.displayNameForTarget(t, targetsWithSubtargets) })));
  }

  displayNameForTarget(target: PatientTargetModel, targetsWithSubtargets: PatientTargetModel[]) {
    if (target.IsTargetSubtarget) {
      const parentTargets = targetsWithSubtargets.filter((t) => t.SubTargets?.some((st) => st.ID === target.ID));
      return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target?.TargetType!) + ')' + ' - (grouped in target(s): ' + parentTargets.map((t) => t.TargetName).join(', ') + ')';
    } else {
      return target.TargetName + ' (' + Utils.patientTargetTypeLabel(target?.TargetType!) + ')';
    }
  }

  // Behavior related methods
  displayNameForBehavior(behavior: PatientBehaviorModel) {
    return behavior.PatientBehaviorName + ' (' + Utils.patientBehaviorTypeLabel(behavior?.BehaviorType!) + ')';
  }

  // Target value methods
  intValueForTarget() {
    if (this.targetIsPromptBased()) {
      return this.addTargetForm.controls.selectedPromptCode.value;
    } else if (this.targetIsFrequency()) {
      return 1;
    } else if (this.targetIsDuration() || this.targetIsToileting()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!);
    } else if (this.targetIsWholePartialInterval()) {
      return this.addTargetForm.controls.happened.value ? 1 : 0;
    } else if (this.targetIsFluencyRate()) {
      return this.addTargetForm.controls.correctCount.value!;
    } else if (this.targetIsIntensity()) {
      return this.addTargetForm.controls.intensityValue.value;
    } else if (this.targetIsRate()) {
      return this.addTargetForm.controls.occurrenceCount.value!;
    } else {
      return this.addTargetForm.controls.textValue.value;
    }
  }

  textValueForTarget() {
    if (this.targetIsPromptBased()) {
      const promptCode = this.selectedTarget()?.PromptCodes?.find((p) => p.ID === this.addTargetForm.controls.selectedPromptCode.value);
      if (this.targetIsEchoic()) {
        return promptCode?.Text! + this.addTargetForm.controls.selectedEchoicRate.value;
      } else {
        return promptCode?.Text;
      }
    } else if (this.targetIsIntValueOnly()) {
      return null;
    } else if (this.targetIsIntensity()) {
      return this.addTargetForm.controls.intensityValue.value?.toString() ?? null;
    } else if (this.targetIsFrequency()) {
      return '1';
    } else if (this.targetIsDuration()) {
      return Utils.formatTime(this.minutesToSeconds(this.addTargetForm.controls.duration.value!));
    } else if (this.targetIsToileting()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!).toString();
    } else if (this.targetIsWholePartialInterval()) {
      return this.addTargetForm.controls.happened.value ? '1' : '0';
    } else if (this.targetIsFluencyRate()) {
      return this.addTargetForm.controls.correctCount.value! + '/' + this.addTargetForm.controls.incorrectCount.value!;
    } else if (this.targetIsRate()) {
      return this.addTargetForm.controls.occurrenceCount.value?.toString() ?? null;
    } else {
      return this.addTargetForm.controls.textValue.value;
    }
  }

  // Behavior value methods
  intValueForBehavior() {
    if (this.behaviorIsFrequency()) {
      return 1;
    } else if (this.behaviorIsDuration()) {
      return this.minutesToSeconds(this.addBehaviorForm.controls.duration.value!);
    } else if (this.behaviorIsInterval()) {
      return this.addBehaviorForm.controls.happenedDuringInterval.value ? 1 : 0;
    } else if (this.behaviorIsIntensity()) {
      return this.addBehaviorForm.controls.intensityValue.value;
    } else {
      return this.addBehaviorForm.controls.textValue.value;
    }
  }

  textValueForBehavior() {
    if (this.behaviorIsFrequency()) {
      return '1';
    } else if (this.behaviorIsDuration()) {
      return Utils.formatTime(this.minutesToSeconds(this.addBehaviorForm.controls.duration.value!));
    } else if (this.behaviorIsInterval()) {
      return this.addBehaviorForm.controls.happenedDuringInterval.value ? '1' : '0';
    } else if (this.behaviorIsIntensity()) {
      return this.addBehaviorForm.controls.intensityValue.value?.toString() ?? null;
    } else {
      return this.addBehaviorForm.controls.textValue.value;
    }
  }

  // Target type checks
  targetIsIntValueOnly() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Quantity;
  }

  targetIsIntensity() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Intensity;
  }

  targetIntensityOptions() {
    const target = this.selectedTarget();
    return Utils.intensityScaleValues(
      target?.IntensityScaleMin,
      target?.IntensityScaleMax,
      target?.IntensityScaleValues
    ).map((value) => ({ label: value.toString(), value }));
  }

  targetIsPromptBased() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return targetIsPromptBased(target);
  }

  targetIsEchoic() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return targetIsEchoic(target);
  }

  targetIsToileting() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Toileting;
  }

  targetIsFrequency() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Frequency;
  }

  targetIsDuration() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Duration;
  }

  targetIsWholePartialInterval() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.WholePartialInterval;
  }

  targetIsFluencyRate() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.FluencyRate;
  }

  targetIsRate() {
    const target = this.selectedTarget();
    if (!target) {
      return false;
    }
    return target?.TargetType === TargetType.Rate;
  }

  targetIsOpenEndedFluencyRate() {
    return isOpenEndedFluencyRate(this.selectedTarget());
  }

  defaultFluencyDuration() {
    const target = this.selectedTarget();
    if (target?.TargetType !== TargetType.FluencyRate && target?.TargetType !== TargetType.Rate) {
      return;
    }

    const durationInMinutes = isOpenEndedFluencyRate(target)
      ? null
      : this.secondsToMinutes(target.Duration ?? 0);
    this.addTargetForm.controls.duration.setValue(durationInMinutes);
  }

  durationForTargetData() {
    if (this.targetIsFluencyRate() || this.targetIsRate()) {
      return this.minutesToSeconds(this.addTargetForm.controls.duration.value!);
    }

    return undefined;
  }

  // Behavior type checks
  behaviorIsFrequency() {
    const behavior = this.selectedBehavior();
    if (!behavior) {
      return false;
    }
    return behavior?.BehaviorType === PatientBehaviorType.Frequency;
  }

  behaviorIsDuration() {
    const behavior = this.selectedBehavior();
    if (!behavior) {
      return false;
    }
    return behavior?.BehaviorType === PatientBehaviorType.Duration;
  }

  behaviorIsInterval() {
    const behavior = this.selectedBehavior();
    if (!behavior) {
      return false;
    }
    return behavior?.BehaviorType === PatientBehaviorType.Interval;
  }

  showFrequencyEndDate() {
    return showBulkFrequencyEndDate(
      this.behaviorIsFrequency(),
      this.addBehaviorForm.controls.frequencyOccurrences.value
    );
  }

  showTargetFrequencyEndDate() {
    return showBulkFrequencyEndDate(
      this.targetIsFrequency(),
      this.addTargetForm.controls.frequencyOccurrences.value
    );
  }

  private updateFrequencyEndDateValidators() {
    updateBulkFrequencyEndDateValidators(
      this.behaviorIsFrequency(),
      this.addBehaviorForm.controls.frequencyOccurrences.value,
      this.addBehaviorForm.controls.frequencyEndDate
    );
  }

  private updateTargetFrequencyEndDateValidators() {
    updateBulkFrequencyEndDateValidators(
      this.targetIsFrequency(),
      this.addTargetForm.controls.frequencyOccurrences.value,
      this.addTargetForm.controls.frequencyEndDate
    );
  }

  behaviorIsIntensity() {
    const behavior = this.selectedBehavior();
    if (!behavior) {
      return false;
    }
    return behavior?.BehaviorType === PatientBehaviorType.Intensity;
  }

  intensityOptions() {
    const behavior = this.selectedBehavior();
    return Utils.intensityScaleValues(
      behavior?.IntensityScaleMin,
      behavior?.IntensityScaleMax,
      behavior?.IntensityScaleValues
    ).map((value) => ({ label: value.toString(), value }));
  }

  // Target methods
  promptOptionsForTarget() {
    const target = this.selectedTarget();
    if (!target) {
      return [];
    }
    const promptOptionsForTarget = target?.PromptCodes?.map((p) => {
      return {
        value: p.ID,
        text: p.Text,
      };
    });
    return promptOptionsForTarget;
  }

  extraOptionsForEchoicTarget() {
    const extraOptionsForEchoicTarget = [
      {
        value: '|Articulation',
        text: 'Articulation',
      },
      {
        value: '|Volume',
        text: 'Volume',
      },
      {
        value: '|Speed',
        text: 'Speed',
      },
      {
        value: '|Tone',
        text: 'Tone',
      },
      {
        value: '|Rhythm',
        text: 'Rhythm',
      },
    ];
    return extraOptionsForEchoicTarget;
  }

  // Submit methods
  onSubmitTarget() {
    this.submitTargetData(true);
  }

  private submitTargetData(closeAfterSave: boolean) {
    console.log(this.addTargetForm.value);

    const rangeError = bulkFrequencyRangeError(
      this.targetIsFrequency(),
      this.addTargetForm.controls.frequencyOccurrences.value,
      this.addTargetForm.controls.frequencyEndDate
    );
    if (rangeError) {
      this.alertService.error(rangeError);
      return;
    }

    // validate form
    if (this.addTargetForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }

    const recordedDateToSend = this.addTargetForm.controls.recordedDate.value ?
      this.addTargetForm.controls.recordedDate.value :
      new Date().toServerString();

    let recordedDates: string[];
    try {
      recordedDates = resolveBulkFrequencyRecordedDates(
        this.targetIsFrequency(),
        recordedDateToSend,
        this.addTargetForm.controls.frequencyEndDate.value,
        this.addTargetForm.controls.frequencyOccurrences.value
      );
    } catch (error) {
      this.alertService.error(
        error instanceof Error ? error.message : 'Unable to add bulk frequency entries.'
      );
      return;
    }

    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }

    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }

    if (this.targetIsFluencyRate() && !this.fluencyRateDataIsValid()) {
      this.alertService.error('Fluency Rate data requires correct count, incorrect count, and duration.');
      return;
    }

    if (this.targetIsRate() && !this.rateDataIsValid()) {
      this.alertService.error('Rate data requires a non-negative occurrence count and a positive duration.');
      return;
    }

    if ((this.intValueForTarget() === null || this.intValueForTarget() === undefined)  && !this.textValueForTarget() ) {
      this.alertService.error('Target data is not valid, please add some value to the target.');
      return;
    }

    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.ID;

    // Get selected staff or use current user
    const selectedStaffValue = this.addTargetForm.controls.selectedStaff.value;
    const staffID = selectedStaffValue?.value?.StaffID ?? this.user?.ID ?? 0;
    const staffFullName = selectedStaffValue?.value?.FullName ?? this.user?.Fullnane ?? '';
    
    // build target data to be saved
    const targetData: PatientTargetDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientTargetID: this.selectedTarget()?.ID ?? 0,
      RecordedDate: recordedDates[0],
      IntValue: Number(this.intValueForTarget()) ?? null,
      TextValue: this.textValueForTarget() ?? null,
      Duration: this.durationForTargetData(),
      UserID: staffID, // Use selected staff from dropdown, or current user
      UserFullName: staffFullName,
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here

    if (this.targetIsToileting()) {
      const toiletingData: PatientTargetDataToileting = {
        UrinationOn: this.urinationOn(),
        UrinationOff: this.urinationOff(),
        BowelMovementOn: this.bowelMovementOn(),
        BowelMovementOff: this.bowelMovementOff(),
        NoVoid: this.noVoid(),
        Duration: this.minutesToSeconds(this.addTargetForm.controls.duration.value!),
      }
      targetData.ToiletingData = toiletingData;
    }

    const targetDataEntries = recordedDates.map((recordedDate) => ({
      ...targetData,
      RecordedDate: recordedDate,
    }));

    console.log(targetDataEntries);
    this.saveTargetData(targetDataEntries, closeAfterSave);
  }

  onSubmitBehavior() {
    this.submitBehaviorData(true);
  }

  private submitBehaviorData(closeAfterSave: boolean) {
    console.log(this.addBehaviorForm.value);

    const rangeError = bulkFrequencyRangeError(
      this.behaviorIsFrequency(),
      this.addBehaviorForm.controls.frequencyOccurrences.value,
      this.addBehaviorForm.controls.frequencyEndDate
    );
    if (rangeError) {
      this.alertService.error(rangeError);
      return;
    }

    // validate form
    if (this.addBehaviorForm.invalid) {
      this.alertService.error('Data is not valid, please check the error messages and try again.');
      return;
    }

    const recordedDateToSend = this.addBehaviorForm.controls.recordedDate.value ?
      this.addBehaviorForm.controls.recordedDate.value :
      new Date().toServerString();

    let recordedDates: string[];
    try {
      recordedDates = resolveBulkFrequencyRecordedDates(
        this.behaviorIsFrequency(),
        recordedDateToSend,
        this.addBehaviorForm.controls.frequencyEndDate.value,
        this.addBehaviorForm.controls.frequencyOccurrences.value
      );
    } catch (error) {
      this.alertService.error(
        error instanceof Error ? error.message : 'Unable to add bulk frequency entries.'
      );
      return;
    }

    if (!this.patientSession && !this.selectedSession && !this.creatingSession()) {
      this.alertService.error('Session is required');
      return;
    }

    if (this.creatingSession() && (!this.newSessionStartDate || !this.newSessionEndDate)) {
      this.alertService.error('Session dates are required');
      return;
    }

    if ((this.intValueForBehavior() === null || this.intValueForBehavior() === undefined) && !this.textValueForBehavior()) {
      this.alertService.error('Behavior data is not valid, please add some value to the behavior.');
      return;
    }

    const sessionIDToSend = this.patientSession ? this.patientSession?.ID : this.selectedSession?.ID;

    // Get selected staff or use current user
    const selectedStaffValue = this.addBehaviorForm.controls.selectedStaff.value;
    const staffID = selectedStaffValue?.value?.StaffID ?? this.user?.ID ?? 0;
    const staffFullName = selectedStaffValue?.value?.FullName ?? this.user?.Fullnane ?? '';
    // build behavior data to be saved
    const behaviorData: PatientBehaviorDataModel = {
      PatientSessionID: sessionIDToSend,
      PatientBehaviorID: this.selectedBehavior()?.ID ?? 0,
      RecordedDate: recordedDates[0],
      IntValue: Number(this.intValueForBehavior()) ?? null,
      TextValue: this.textValueForBehavior() ?? null,
      UserID: staffID, // Use selected staff from dropdown, or current user
      UserFullName: staffFullName,
      PatientBehaviorAntecedentIDs: this.selectedAntecedents()?.map((antecedent) => antecedent.ID!).filter((id) => id != null) ?? [],
      PatientBehaviorConsequenceIDs: this.selectedConsequences()?.map((consequence) => consequence.ID!).filter((id) => id != null) ?? [],
      PatientBehaviorSeverityID: this.addBehaviorForm.controls.selectedSeverity.value?.ID ?? null,
      PatientBehaviorResponseID: this.addBehaviorForm.controls.selectedResponse.value?.ID ?? null,
      PatientBehaviorLocationID: this.addBehaviorForm.controls.selectedLocation.value?.ID ?? null,
      Notes: this.addBehaviorForm.controls.notes.value ?? null,
    }
    
    // Note: ModifiedByUserID, ModifiedDate, ManuallyAddedByUserID, and ManuallyAddedDate 
    // are all set by the backend and should not be set here

    const behaviorDataEntries = recordedDates.map((recordedDate) => ({
      ...behaviorData,
      RecordedDate: recordedDate,
      PatientBehaviorAntecedentIDs: [...(behaviorData.PatientBehaviorAntecedentIDs ?? [])],
      PatientBehaviorConsequenceIDs: [...(behaviorData.PatientBehaviorConsequenceIDs ?? [])],
    }));

    console.log(behaviorDataEntries);
    this.saveBehaviorData(behaviorDataEntries, closeAfterSave);
  }

  // Save methods
  saveTargetData(targetDataEntries: PatientTargetDataModel[], closeAfterSave: boolean) {
    this.isLoading.set(true);
    console.log('Saving target data:', targetDataEntries);

    if (this.creatingSession()) {
      this.staffApiService.SavePatientSession({
        body: {
          PatientID: this.selectedPatientID,
          StartedDate: this.newSessionStartDate!,
          FinishedDate: this.newSessionEndDate!,
        },
      }).subscribe(
        {
          next: (response) => {
            console.log('Created session:', response);
            targetDataEntries.forEach((targetData) => {
              targetData.PatientSessionID = response.ID;
            });
            this.doSaveTargetData(targetDataEntries, closeAfterSave);
          },
          error: (error) => {
            console.error('Error creating session:', error);
            this.alertService.error('Error creating session - ' + Utils.getAjaxError(error));
            this.isLoading.set(false);
          }
        }
      );
    } else {
      this.doSaveTargetData(targetDataEntries, closeAfterSave);
    }
  }

  doSaveTargetData(targetDataEntries: PatientTargetDataModel[], closeAfterSave: boolean) {
    this.isLoading.set(true);
    
    const currentUser = this.user;
    const currentUserID = currentUser?.ID;
    targetDataEntries.forEach((targetData) => {
      if (!targetData.UserID && currentUserID) {
        targetData.UserID = currentUserID;
        targetData.UserFullName = currentUser?.Fullnane ?? '';
      }

      if (!this.patientSession && this.selectedSession && !this.creatingSession() && currentUserID) {
        targetData.ManuallyAddedByUserID = currentUserID;
        targetData.ManuallyAddedDate = new Date().toISOString();
      }
    });

    this.saveFrequencyEntries(
      'target',
      targetDataEntries,
      (entry) => bulkFrequencySaveKey('target', entry.PatientSessionID, entry.PatientTargetID, entry.RecordedDate),
      (targetData) => this.staffApiService.SavePatientTargetData({
        body: targetData,
        addedManually: true,
      }),
      closeAfterSave,
      () => this.resetTargetFormAfterSave()
    );
  }

  saveBehaviorData(behaviorDataEntries: PatientBehaviorDataModel[], closeAfterSave: boolean) {
    this.isLoading.set(true);
    console.log('Saving behavior data:', behaviorDataEntries);

    if (this.creatingSession()) {
      this.staffApiService.SavePatientSession({
        body: {
          PatientID: this.selectedPatientID,
          StartedDate: this.newSessionStartDate!,
          FinishedDate: this.newSessionEndDate!,
        },
      }).subscribe(
        {
          next: (response) => {
            console.log('Created session:', response);
            behaviorDataEntries.forEach((behaviorData) => {
              behaviorData.PatientSessionID = response.ID;
            });
            this.doSaveBehaviorData(behaviorDataEntries, closeAfterSave);
          },
          error: (error) => {
            console.error('Error creating session:', error);
            this.alertService.error('Error creating session - ' + Utils.getAjaxError(error));
            this.isLoading.set(false);
          }
        }
      );
    } else {
      this.doSaveBehaviorData(behaviorDataEntries, closeAfterSave);
    }
  }

  doSaveBehaviorData(behaviorDataEntries: PatientBehaviorDataModel[], closeAfterSave: boolean) {
    this.isLoading.set(true);
    
    // UserID should already be set (from dropdown or current user)
    // If not set, use current user as fallback
    const currentUser = this.user;
    const currentUserID = currentUser?.ID;
    behaviorDataEntries.forEach((behaviorData) => {
      if (!behaviorData.UserID && currentUserID) {
        behaviorData.UserID = currentUserID;
        behaviorData.UserFullName = currentUser?.Fullnane ?? '';
      }

      if (!this.patientSession && this.selectedSession && !this.creatingSession() && currentUserID) {
        behaviorData.ManuallyAddedByUserID = currentUserID;
        behaviorData.ManuallyAddedDate = new Date().toISOString();
      }
    });

    this.saveFrequencyEntries(
      'behavior',
      behaviorDataEntries,
      (entry) => bulkFrequencySaveKey('behavior', entry.PatientSessionID, entry.PatientBehaviorID, entry.RecordedDate),
      (behaviorData) => this.staffApiService.SavePatientBehaviorData({
        body: behaviorData,
        addedManually: true,
      }),
      closeAfterSave,
      () => this.resetBehaviorFormAfterSave()
    );
  }

  private saveFrequencyEntries<T>(
    noun: 'behavior' | 'target',
    entries: T[],
    keyOf: (entry: T) => string,
    save: (entry: T) => Observable<unknown>,
    closeAfterSave: boolean,
    resetAfterSave: () => void
  ): void {
    saveBulkFrequencyEntries(entries, this.savedFrequencyKeys, keyOf, save).subscribe({
      next: (result) => {
        if (result.skippedAll) {
          this.alertService.error(ALREADY_SAVED_BULK_FREQUENCY_MESSAGE);
          this.isLoading.set(false);
          return;
        }
        this.savedFrequencyKeys.clear();
        this.alertService.success(bulkFrequencySuccessMessage(noun, result.requestedCount));
        this.isLoading.set(false);
        if (!closeAfterSave) {
          resetAfterSave();
          return;
        }
        this.close.emit(true);
      },
      error: (error) => {
        this.alertService.error(
          bulkFrequencyErrorMessage(
            noun,
            Utils.getAjaxError(error),
            entries.length,
            this.savedFrequencyKeys.size,
            entries.length
          )
        );
        this.isLoading.set(false);
      }
    });
  }

  private resetTargetFormAfterSave() {
    const selectedTarget = this.selectedTarget();
    this.addTargetForm.reset();
    this.addTargetForm.controls.recordedDate.setValue(this.getSessionStartDateForRecordedDate());
    this.addTargetForm.controls.frequencyOccurrences.setValue(1);
    if (selectedTarget) {
      this.addTargetForm.controls.target.setValue(selectedTarget);
    }
    this.updateTargetFrequencyEndDateValidators();
  }

  private resetBehaviorFormAfterSave() {
    const selectedBehavior = this.selectedBehavior();
    this.addBehaviorForm.reset();
    this.addBehaviorForm.controls.recordedDate.setValue(this.getSessionStartDateForRecordedDate());
    this.addBehaviorForm.controls.frequencyOccurrences.setValue(1);
    this.addBehaviorForm.controls.selectedAntecedent.setValue([]);
    this.addBehaviorForm.controls.selectedConsequence.setValue([]);
    if (selectedBehavior) {
      this.addBehaviorForm.controls.behavior.setValue(selectedBehavior);
    }
    this.selectedAntecedents.set([]);
    this.selectedConsequences.set([]);
    this.updateFrequencyEndDateValidators();
  }

  // Utility methods
  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }

  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  onCancel() {
    this.close.emit(true);
  }

  changedTarget(event: any) {
    if (event.value) {
      this.selectedTarget.set(event.value.value);
      this.defaultFluencyDuration();
      this.loadTargetStaffMembersForSelection();
      this.updateTargetFrequencyEndDateValidators();
    }
  }

  private fluencyRateDataIsValid() {
    const correctCount = this.addTargetForm.controls.correctCount.value;
    const incorrectCount = this.addTargetForm.controls.incorrectCount.value;
    const duration = this.addTargetForm.controls.duration.value;

    return (
      correctCount !== null &&
      correctCount !== undefined &&
      incorrectCount !== null &&
      incorrectCount !== undefined &&
      duration !== null &&
      duration !== undefined &&
      duration > 0
    );
  }

  private rateDataIsValid() {
    const occurrenceCount = this.addTargetForm.controls.occurrenceCount.value;
    const duration = this.addTargetForm.controls.duration.value;

    return (
      occurrenceCount !== null &&
      occurrenceCount !== undefined &&
      occurrenceCount >= 0 &&
      duration !== null &&
      duration !== undefined &&
      this.minutesToSeconds(duration) > 0
    );
  }
  
  changedBehavior(event: any) {
    if (event.value) {
      this.selectedBehavior.set(event.value.value);
      this.loadBehaviorStaffMembersForSelection();
      this.updateFrequencyEndDateValidators();
    }
  }

  // Tab change handler (optional, for future use)
  onTabChange(event: any) {
    this.activeTabIndex = event.index;
  }

  // Custom tab switching method
  setActiveTab(index: number) {
    this.activeTabIndex = index;
  }
  
  loadTargetStaffMembersForSelection() {
    let selectedSessionValue: PatientSessionModel | null = this.selectedSession;

    // if no session is selected, just return;
    if (!selectedSessionValue) {
      return;
    } 
    
    if (!selectedSessionValue.ServiceID) {
      // if session has no service, just use the current user
      const currentUserStaff: DataTakerStaffDataModel = {
        StaffID: this.user?.ID ?? 0,
        FullName: this.user?.Fullnane ?? '',
        Email: null
      };
      this.targetStaffMembers.set([currentUserStaff]);
      this.updateTargetStaffOptions();
      
      // Preselect current user
      this.preselectTargetCurrentUser();
    } else if (selectedSessionValue.StaffIDs && selectedSessionValue.StaffIDs.length > 0) {
        // if session has a service, get the staff from the session(which came from the service)
        // just get the first one for now
        const staffID = selectedSessionValue.StaffIDs[0];
        const staffFullName = selectedSessionValue.StaffNames?.[0] ?? '';
        const staff: DataTakerStaffDataModel = {
          StaffID: staffID,
          FullName: staffFullName,
          Email: null
        };
        this.targetStaffMembers.set([staff]);
        this.updateTargetStaffOptions();
        
        // preselect staff from session
        this.preselectFirstTargetStaffFromSession([staff]);
    }
  }

  preselectTargetCurrentUser() {
    if (this.user && this.user.ID) {
      const currentUserOption = this.targetStaffOptions().find(s => s.value?.StaffID === this.user!.ID);
      if (currentUserOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: currentUserOption.label,
          value: currentUserOption.value
        };
        this.addTargetForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }

  preselectFirstTargetStaffFromSession(staff: DataTakerStaffDataModel[]) {
    if (staff && staff.length > 0) {
      const staffOption = this.targetStaffOptions().find(s => s.value?.StaffID === staff[0].StaffID);
      if (staffOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: staffOption.label,
          value: staffOption.value
        };
        this.addTargetForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }
  
  updateTargetStaffOptions() {
    this.targetStaffOptions.set(
      this.targetStaffMembers().map(staff => ({
        label: staff.FullName ?? '',
        value: staff
      }))
    );
  }

  loadBehaviorStaffMembersForSelection() {
    let selectedSessionValue: PatientSessionModel | null = this.selectedSession;

    // if no session is selected, just return;
    if (!selectedSessionValue) {
      return;
    } 
    
    if (!selectedSessionValue.ServiceID) {
      // if session has no service, just use the current user
      const currentUserStaff: DataTakerStaffDataModel = {
        StaffID: this.user?.ID ?? 0,
        FullName: this.user?.Fullnane ?? '',
        Email: null
      };
      this.behaviorStaffMembers.set([currentUserStaff]);
      this.updateBehaviorStaffOptions();
      
      // Preselect current user
      this.preselectBehaviorCurrentUser();
    } else if (selectedSessionValue.StaffIDs && selectedSessionValue.StaffIDs.length > 0) {
        // just get the first one for now
        const staffID = selectedSessionValue.StaffIDs[0];
        const staffFullName = selectedSessionValue.StaffNames?.[0] ?? '';
        const staff: DataTakerStaffDataModel = {
          StaffID: staffID,
          FullName: staffFullName,
          Email: null
        };
        this.behaviorStaffMembers.set([staff]);
        this.updateBehaviorStaffOptions();
        
        // preselect staff from session
        this.preselectFirstBehaviorStaffFromSession([staff]);
    }
  }

  preselectBehaviorCurrentUser() {
    if (this.user && this.user.ID) {
      const currentUserOption = this.behaviorStaffOptions().find(s => s.value?.StaffID === this.user!.ID);
      if (currentUserOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: currentUserOption.label,
          value: currentUserOption.value
        };
        this.addBehaviorForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }

  preselectFirstBehaviorStaffFromSession(staff: DataTakerStaffDataModel[]) {
    if (staff && staff.length > 0) {
      const staffOption = this.behaviorStaffOptions().find(s => s.value?.StaffID === staff[0].StaffID);
      if (staffOption) {
        const selectedItemStaff: SelectItem<DataTakerStaffDataModel> = {
          label: staffOption.label,
          value: staffOption.value
        };
        this.addBehaviorForm.controls.selectedStaff.setValue(selectedItemStaff);
      }
    }
  }
  
  updateBehaviorStaffOptions() {
    this.behaviorStaffOptions.set(
      this.behaviorStaffMembers().map(staff => ({
        label: staff.FullName ?? '',
        value: staff
      }))
    );
  }

  // Helper method to get session start date for recorded date
  private getSessionStartDateForRecordedDate(): string {
    if (this.patientSession?.StartedDate) {
      const sessionStartDate = new Date(this.patientSession.StartedDate);
      return new Date(sessionStartDate.getTime() - (sessionStartDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    } else if (this.selectedSession?.StartedDate) {
      const sessionStartDate = new Date(this.selectedSession.StartedDate);
      return new Date(sessionStartDate.getTime() - (sessionStartDate.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    } else {
      // Fallback to current date if no session is available
      const now = new Date();
      return new Date(now.getTime() - (now.getTimezoneOffset() * 60000)).toISOString().slice(0, 16);
    }
  }

  // Save and Add More for Target
  onSaveAndAddMoreTarget() {
    this.submitTargetData(false);
  }

  // Save and Add More for Behavior
  onSaveAndAddMoreBehavior() {
    this.submitBehaviorData(false);
  }

  selectedSessionLabel(session: PatientSessionModel | null) {
    if (session) {
      return 'Session: ' + this.formattedDates(session.StartedDate, session.FinishedDate) + (session.ServiceID ? (' (Service: #' + session.ServiceID + ' - ' + session.StaffName) : '' ) + ')';
    } else {
      return '';
    }
  }

  truncateMiddle(value: string | null | undefined, maxLength: number = 90): string {
    if (!value) {
      return '';
    }

    if (value.length <= maxLength) {
      return value;
    }

    const separator = '...';
    const available = maxLength - separator.length;
    const startLength = Math.ceil(available / 2);
    const endLength = Math.floor(available / 2);

    return `${value.slice(0, startLength)}${separator}${value.slice(value.length - endLength)}`;
  }

  shouldShowTargetStaffSelector() {
    return !this.hideTargetSelector && this.selectedTarget() !== null && this.selectedTarget() !== undefined && !this.creatingSession();
  }

  shouldShowBehaviorStaffSelector() {
    return !this.hideBehaviorSelector && this.selectedBehavior() !== null && this.selectedBehavior() !== undefined && !this.creatingSession();
  }
}
